import { useEffect, useMemo, useState } from 'react'
import { renderTierValue } from '@shared/capabilities'
import { CAPABILITY_CATEGORIES, Capability, tierDefault } from '../lib/capabilityCatalog'
import { useRemoteUsers } from '../lib/useRemoteData'
import { AdminPlan, PlanPatch, useServerOverrides } from '../lib/overrides'
import { listPublicPlans, type RemotePublicPlan } from '../lib/adminClient'
import { LoadError } from '../components/LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { DrillValue } from '../components/Drill'
import type { AdminUser } from '../lib/domain'
import { TIER_IDS, TIER_LADDER, TierId, tierName, tierTone } from '../lib/tiers'
import { isCreditFunded, isGranted, mrrBasis, mrrLine, summariseMrr } from '../lib/revenue'
import { day, num, usd } from '../lib/format'

// Pricing and access for the growth-spec ladder: Free, Pro $12, Pro+ $25,
// Team $15 per seat, Enterprise custom (yearly 120 / 250 / 150).
//
// Three sources are shown side by side, so a mismatch is visible instead of
// hidden: the CONTRACT (lib/tiers.ts, the spec), the stored OVERRIDES
// (/admin/plans), and what the server LIVE-serves the brochure
// (/public/plans = the server's own defaults + overrides).

type LivePlans = { state: 'loading' } | { state: 'ready'; plans: RemotePublicPlan[] } | { state: 'error'; error: string }

function money(n: number | null): string {
  return n === null ? 'custom' : usd(n)
}

function PlanEditor({
  plan,
  override,
  live,
  onPatch,
  disabled
}: {
  plan: AdminPlan
  override: PlanPatch | undefined
  live: LivePlans
  onPatch: (p: PlanPatch) => void
  disabled: boolean
}) {
  const spec = TIER_LADDER.find((t) => t.id === plan.id)!
  const custom = spec.priceMonthly === null
  const [draftMonthly, setDraftMonthly] = useState(String(plan.priceMonthly ?? ''))
  const [draftYearly, setDraftYearly] = useState(String(plan.priceYearly ?? ''))
  const [draftTagline, setDraftTagline] = useState(plan.tagline)
  const [inputError, setInputError] = useState<string | null>(null)

  useEffect(() => { setDraftMonthly(String(plan.priceMonthly ?? '')) }, [plan.priceMonthly])
  useEffect(() => { setDraftYearly(String(plan.priceYearly ?? '')) }, [plan.priceYearly])
  useEffect(() => { setDraftTagline(plan.tagline) }, [plan.tagline])

  // Send only what changed. Writing every field on every blur used to turn an
  // untouched price into a stored override, which then pins the brochure to
  // that number after the defaults move (exactly what a price change does).
  function commit() {
    const patch: PlanPatch = {}
    setInputError(null)
    if (!custom) {
      const monthly = Number(draftMonthly)
      const yearly = Number(draftYearly)
      if (!Number.isFinite(monthly) || !Number.isFinite(yearly) || monthly < 0 || yearly < 0 || draftMonthly.trim() === '' || draftYearly.trim() === '') {
        setInputError('Prices must be numbers of 0 or more.')
        return
      }
      if (monthly !== plan.priceMonthly) patch.priceMonthly = monthly
      if (yearly !== plan.priceYearly) patch.priceYearly = yearly
    }
    if (draftTagline !== plan.tagline) patch.tagline = draftTagline
    if (Object.keys(patch).length > 0) onPatch(patch)
  }

  const livePlan = live.state === 'ready' ? live.plans.find((p) => p.id === plan.id) : undefined
  const staleOverride =
    !custom &&
    override &&
    ((override.priceMonthly !== undefined && override.priceMonthly !== spec.priceMonthly) ||
      (override.priceYearly !== undefined && override.priceYearly !== spec.priceYearly))
  const liveDiffers =
    livePlan && !custom && (livePlan.priceMonthly !== plan.priceMonthly || livePlan.priceYearly !== plan.priceYearly)

  return (
    <div className="glass-card rounded-xl p-5 flex flex-col" data-testid={`plan-${plan.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">
            {spec.unit === 'seat' ? 'Per seat' : spec.unit === 'custom' ? 'Contact sales' : 'Per user'}
          </div>
          <div className="text-xl font-semibold mt-1">{plan.name}</div>
        </div>
        <label className="text-xs flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={plan.active} disabled={disabled} onChange={() => onPatch({ active: !plan.active })} className="accent-violet-500" />
          Listed
        </label>
      </div>

      <div className="text-xs text-slate-300 mb-3 space-y-0.5" data-testid={`plan-contract-${plan.id}`}>
        <div>
          Ladder: <span className="text-white">{money(spec.priceMonthly)}{custom ? '' : ' / month'}</span>
          {!custom && <> · <span className="text-white">{money(spec.priceYearly)} / year</span></>}
        </div>
        <div className="text-slate-400">
          AI: {spec.allowanceCredits === null ? 'custom allowance' : `${num(spec.allowanceCredits)} credits / month${spec.unit === 'seat' ? ' per seat, pooled' : ''}`}
          {' · '}
          {spec.models === 'all' ? 'all models' : spec.models.map((m) => m[0].toUpperCase() + m.slice(1)).join(', ')}
          {spec.maxTokens ? ` · max_tokens ${num(spec.maxTokens)}` : ''}
        </div>
      </div>

      <label className="block mb-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Tagline (shown on brochure card)</div>
        <input value={draftTagline} onChange={(e) => setDraftTagline(e.target.value)} onBlur={commit} disabled={disabled}
          className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent" />
      </label>
      {custom ? (
        <div className="text-xs text-slate-500 mb-3">Price is set per contract; there is no list price to edit.</div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">$ / {spec.unit} / month</div>
            <input value={draftMonthly} onChange={(e) => setDraftMonthly(e.target.value)} onBlur={commit} disabled={disabled}
              aria-label={`${plan.name} monthly price`}
              className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">$ / {spec.unit} / year</div>
            <input value={draftYearly} onChange={(e) => setDraftYearly(e.target.value)} onBlur={commit} disabled={disabled}
              aria-label={`${plan.name} yearly price`}
              className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent" />
          </label>
        </div>
      )}
      {inputError && <div className="text-xs text-red-300 mb-2">{inputError}</div>}

      <div className="mt-auto space-y-1.5 text-[11px]">
        {staleOverride && (
          <div className="text-amber-300" data-testid={`plan-stale-override-${plan.id}`}>
            A stored override ({money(override?.priceMonthly ?? plan.priceMonthly)} / {money(override?.priceYearly ?? plan.priceYearly)}) differs from the ladder.
            <button
              className="ml-1 underline hover:text-amber-100"
              disabled={disabled}
              onClick={() => onPatch({ priceMonthly: spec.priceMonthly!, priceYearly: spec.priceYearly! })}
            >
              Apply ladder price
            </button>
          </div>
        )}
        {live.state === 'loading' && <div className="text-slate-500">Checking what the brochure serves…</div>}
        {live.state === 'error' && <div className="text-red-300">Could not read /public/plans: {live.error}</div>}
        {live.state === 'ready' && !livePlan && (
          <div className="text-amber-300" data-testid={`plan-not-served-${plan.id}`}>
            The server does not serve {plan.name} on /public/plans yet.
          </div>
        )}
        {livePlan && !custom && (
          <div className={liveDiffers ? 'text-amber-300' : 'text-emerald-300'} data-testid={`plan-live-${plan.id}`}>
            Brochure serves {money(livePlan.priceMonthly)} / {money(livePlan.priceYearly)}
            {liveDiffers ? ': the server’s plan defaults are not on the new ladder yet.' : ': matches.'}
          </div>
        )}
        {livePlan && custom && <div className="text-emerald-300">Served on /public/plans.</div>}
      </div>
    </div>
  )
}

function MatrixCell({
  value,
  inherited,
  onChange,
  disabled
}: {
  value: boolean | number | string
  inherited: string | null
  onChange: (next: boolean | number | string) => void
  disabled: boolean
}) {
  const r = renderTierValue(value)
  const title = inherited ? `Inherits ${inherited} until set here` : undefined

  if (typeof value === 'boolean') {
    return (
      <button
        onClick={() => onChange(!value)}
        disabled={disabled}
        className={`inline-flex items-center justify-center w-7 h-7 rounded border transition-colors ${
          value ? 'bg-accent/20 border-accent text-accent' : 'bg-white/5 border-white/10 text-slate-500 hover:border-white/30'
        } ${inherited ? 'border-dashed opacity-70' : ''}`}
        title={title ?? (value ? 'Included — click to disable' : 'Not included — click to enable')}
      >
        {value ? '✓' : ''}
      </button>
    )
  }

  return (
    <input
      defaultValue={String(value)}
      key={String(value)}
      disabled={disabled}
      onBlur={(e) => {
        const txt = e.target.value
        if (txt === String(value)) return
        const asNum = Number(txt)
        onChange(txt.trim() !== '' && Number.isFinite(asNum) ? asNum : txt)
      }}
      className={`w-20 bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-center focus:outline-none focus:border-accent ${inherited ? 'border-dashed opacity-70' : ''}`}
      title={title ?? `Currently "${r.text}"`}
    />
  )
}

// Who is behind one tier's row: each paying account with what it is charged,
// then the accounts on that tier by referral credit or by hand.
function TierDetail({ paying, credit, granted }: { paying: AdminUser[]; credit: AdminUser[]; granted: AdminUser[] }): JSX.Element {
  if (paying.length + credit.length + granted.length === 0) {
    return <div className="text-xs text-slate-400">Nobody is on this tier.</div>
  }
  const payLine = (u: AdminUser): string => {
    const l = mrrLine(u)
    const b = u.billing
    const parts = [
      b?.interval === 'year' ? 'yearly' : b?.interval === 'month' ? 'monthly' : 'interval not reported',
      l ? `${usd(l.monthlyUsd)}/month${l.exact ? '' : ' at list price'}` : 'custom price, not counted',
      b?.grandfathered === true ? 'grandfathered' : null,
      b?.subscriptionStatus ?? null,
      b?.subscriptionId ?? null
    ]
    return parts.filter(Boolean).join(' · ')
  }
  return (
    <DetailGrid
      facts={[]}
      wide={[
        ['Paying by card', paying.length ? paying.map((u) => `${u.email}: ${payLine(u)}`).join('\n') : 'Nobody'],
        ['Paid from referral credit', credit.length ? credit.map((u) => `${u.email}: until ${day(u.referrals?.creditPaidUntil ?? null)}`).join('\n') : 'Nobody'],
        ['Granted by an admin, no charge', granted.length ? granted.map((u) => u.email).join('\n') : 'Nobody']
      ]}
    />
  )
}

// Paying subscribers grouped by tier: interval mix, grandfathered count and
// MRR, from live Stripe state only.
function SubscribersSection() {
  const { data: users, error, loading } = useRemoteUsers()
  const rowOpen = useOpenRow()
  if (error) return <LoadError what="subscribers" endpoint="GET /admin/users" error={error} />
  if (loading) return <div className="glass-card rounded-xl p-5 text-sm text-slate-400">Loading subscribers…</div>

  const mrr = summariseMrr(users)
  const intervalReported = users.some((u) => u.billing?.paying && u.billing.interval !== undefined)
  const grandfatherReported = users.some((u) => u.billing?.paying && u.billing.grandfathered !== undefined)
  const rows = TIER_IDS.map((tier) => {
    const paying = users.filter((u) => u.status === 'active' && u.billing?.paying === true && (u.billing.plan ?? u.tier) === tier)
    const lines = paying.map(mrrLine)
    return {
      tier,
      payingUsers: paying,
      grantedUsers: users.filter((u) => isGranted(u) && u.tier === tier),
      creditUsers: users.filter((u) => isCreditFunded(u) && u.referrals?.planFundedByCredit === tier),
      paying: paying.length,
      monthly: paying.filter((u) => u.billing?.interval === 'month').length,
      yearly: paying.filter((u) => u.billing?.interval === 'year').length,
      grandfathered: paying.filter((u) => u.billing?.grandfathered === true).length,
      grandfatheredUsers: paying.filter((u) => u.billing?.grandfathered === true),
      granted: users.filter((u) => isGranted(u) && u.tier === tier).length,
      creditFunded: users.filter((u) => isCreditFunded(u) && u.referrals?.planFundedByCredit === tier).length,
      mrr: lines.reduce((s, l) => s + (l?.monthlyUsd ?? 0), 0),
      estimated: lines.filter((l) => l && !l.exact).length,
      unpriced: lines.filter((l) => l === null).length
    }
  })
  const knownTiers = new Set<string>(TIER_IDS)
  const otherPaying = users.filter((u) => u.billing?.paying === true && !knownTiers.has(u.billing.plan ?? u.tier)).length

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <table className="admin-table" data-testid="subscribers-by-tier">
        <thead>
          <tr>
            <th>Tier</th>
            <th className="text-right">Paying</th>
            <th className="text-right">Monthly / yearly</th>
            <th className="text-right">Grandfathered</th>
            <th className="text-right">Paid by referral credit</th>
            <th className="text-right">Granted (no charge)</th>
            <th className="text-right">MRR</th>
          </tr>
        </thead>
        <tbody>
          {/* A tier opens to the accounts behind its numbers. */}
          {rows.map((r) => (
            <ExpandableRow
              key={r.tier}
              open={rowOpen.isOpen(r.tier)}
              onToggle={() => rowOpen.toggle(r.tier)}
              colSpan={7}
              testId={`tier-row-${r.tier}`}
              detail={() => <TierDetail paying={r.payingUsers} credit={r.creditUsers} granted={r.grantedUsers} />}
            >
              <td><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierTone(r.tier)}`}>{tierName(r.tier)}</span></td>
              <td className="text-right tabular-nums">
                <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, paying: '1' } }} label={`Paying ${tierName(r.tier)} accounts`} expect={{ key: 'count', value: r.paying }} testId={`drill-paying-${r.tier}`}>
                  {r.paying}
                </DrillValue>
              </td>
              <td className="text-right tabular-nums">
                {intervalReported ? (
                  <>
                    <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, paying: '1', interval: 'month' } }} label={`${tierName(r.tier)} accounts paying monthly`} expect={{ key: 'count', value: r.monthly }}>{r.monthly}</DrillValue>
                    {' / '}
                    <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, paying: '1', interval: 'year' } }} label={`${tierName(r.tier)} accounts paying yearly`} expect={{ key: 'count', value: r.yearly }}>{r.yearly}</DrillValue>
                  </>
                ) : (
                  <span className="text-slate-500">not reported</span>
                )}
              </td>
              <td className="text-right tabular-nums" data-testid={`grandfathered-count-${r.tier}`}>
                {/* Grandfathered is judged against the list price table, which
                    only the users list applies, so it opens those accounts by id. */}
                {grandfatherReported ? (
                  <DrillValue to={{ set: 'accounts', filters: { ids: r.grandfatheredUsers.map((u) => u.id).join(',') } }} label={`Grandfathered ${tierName(r.tier)} accounts`} expect={{ key: 'count', value: r.grandfathered }}>
                    {r.grandfathered}
                  </DrillValue>
                ) : (
                  <span className="text-slate-500">not reported</span>
                )}
              </td>
              <td className="text-right tabular-nums" data-testid={`credit-funded-count-${r.tier}`}>
                <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, creditFunded: '1' } }} label={`${tierName(r.tier)} paid from referral credit`} expect={{ key: 'count', value: r.creditFunded }}>
                  {r.creditFunded}
                </DrillValue>
              </td>
              <td className="text-right tabular-nums">
                <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, granted: '1' } }} label={`${tierName(r.tier)} granted with no charge`} expect={{ key: 'count', value: r.granted }}>
                  {r.granted}
                </DrillValue>
              </td>
              <td className="text-right tabular-nums">
                <DrillValue to={{ set: 'accounts', filters: { tier: r.tier, paying: '1' } }} label={`Paying ${tierName(r.tier)} accounts behind ${usd(r.mrr)} MRR`} expect={{ key: 'count', value: r.paying }}>
                  {usd(r.mrr)}
                </DrillValue>
                {r.estimated > 0 && <div className="text-[10px] text-slate-500">{r.estimated} at list price</div>}
                {r.unpriced > 0 && <div className="text-[10px] text-slate-500">{r.unpriced} custom, not counted</div>}
              </td>
            </ExpandableRow>
          ))}
        </tbody>
      </table>
      <div className="px-4 py-3 text-xs text-slate-400 border-t border-white/5">
        Total{' '}
        <DrillValue to={{ set: 'accounts', filters: { paying: '1' } }} label={`Paying accounts behind ${usd(mrr.totalUsd)} MRR`} expect={{ key: 'count', value: mrr.paying }}>
          {usd(mrr.totalUsd)} MRR
        </DrillValue>{' '}
        · {mrrBasis(mrr)}
        {otherPaying > 0 && ` · ${otherPaying} on a tier this console does not know`}
        {!grandfatherReported && mrr.paying > 0 && ' · The server does not report charged prices yet, so grandfathered subscribers ($9.95 / $14.95) are counted at the new list price and MRR is overstated by the difference.'}
      </div>
    </div>
  )
}

export default function Subscriptions() {
  const {
    plans,
    capabilities,
    planOverrides,
    capOverrides,
    loading,
    error,
    saving,
    justSaved,
    patchPlan,
    patchCapability,
    resetAll
  } = useServerOverrides()
  const [search, setSearch] = useState('')
  const [showOnlyOverridden, setShowOnlyOverridden] = useState(false)
  const [live, setLive] = useState<LivePlans>({ state: 'loading' })

  const loadLive = () =>
    listPublicPlans().then((res) =>
      setLive(res.ok ? { state: 'ready', plans: res.data.plans ?? [] } : { state: 'error', error: res.error })
    )
  useEffect(() => {
    void loadLive()
  }, [])
  // After a save the brochure's answer changes; re-read it so the drift line is
  // current (the endpoint is edge-cached ~60s, so it may lag a save briefly).
  useEffect(() => {
    if (justSaved) void loadLive()
  }, [justSaved])

  function handleResetAll() {
    if (!confirm('Reset every plan + capability override back to the defaults?')) return
    void resetAll()
  }

  const totalCapabilities = capabilities.length
  const overrideCount = Object.keys(capOverrides).length + Object.keys(planOverrides).length

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase()
    return CAPABILITY_CATEGORIES.map((cat) => {
      const items = capabilities
        .filter((c) => c.categoryKey === cat.key)
        .filter((c) => {
          if (showOnlyOverridden && !capOverrides[c.key]) return false
          if (!q) return true
          return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q) || c.key.includes(q)
        })
      return { cat, items }
    }).filter((g) => g.items.length > 0)
  }, [capabilities, search, showOnlyOverridden, capOverrides])

  // Pro+ inherits Pro and Enterprise inherits Team until a row or an override
  // sets them (growth spec section 1). Shown dashed so the inheritance is clear.
  function inheritedFrom(cap: Capability, tier: TierId): string | null {
    if (tier !== 'proplus' && tier !== 'enterprise') return null
    if (cap.defaults[tier] !== undefined) return null
    return tier === 'proplus' ? 'Pro' : 'Team'
  }

  const matrixCols = { gridTemplateColumns: `minmax(16rem, 1fr) repeat(${TIER_IDS.length}, 5.5rem)` }
  // Writes are blocked until the stored overrides have loaded: an edit made
  // against the un-merged defaults could silently replace a stored value.
  const writesBlocked = loading || !!(error && error.startsWith("Couldn't load"))

  return (
    <div>
      {justSaved && (
        <div className="fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-sm shadow-lg">
          <span className="text-emerald-400">✓</span>
          Saved on the server. The brochure reads it from <code className="text-emerald-100">/public/plans</code> within about a minute.
        </div>
      )}

      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Pricing & access</div>
          <h1 className="text-2xl font-semibold mt-1">Subscriptions & permissions</h1>
          <div className="text-sm text-slate-400 mt-1">
            {TIER_LADDER.length} plans · {totalCapabilities} capabilities · {overrideCount} {overrideCount === 1 ? 'override' : 'overrides'}
            {loading && <span className="ml-2 text-slate-500">· loading…</span>}
            {saving && <span className="ml-2 text-accent">· saving…</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={handleResetAll} disabled={overrideCount === 0 || saving || loading}>Reset overrides</button>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-xl p-4 mb-6 text-sm bg-red-500/10 border border-red-500/30 text-red-200" data-testid="overrides-error">
          {error}
        </div>
      )}

      <div className="glass-card rounded-xl p-4 mb-6 text-xs text-slate-300">
        Edits save when you leave a field. Prices and taglines drive the brochure at <code className="text-accent">/pricing</code>;
        capability toggles also gate the desktop app per tier. New prices apply to new checkouts only: existing subscribers keep their price until they change plan.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        {plans.map((p) => (
          <PlanEditor
            key={p.id}
            plan={p}
            override={planOverrides[p.id]}
            live={live}
            disabled={writesBlocked || saving}
            onPatch={(patch) => patchPlan(p.id, patch)}
          />
        ))}
      </div>

      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Subscribers</div>
        <div className="text-sm text-slate-400 mt-0.5">Live Stripe subscriptions by tier. Grandfathered = kept an older, lower price.</div>
      </div>
      <div className="mb-8">
        <SubscribersSection />
      </div>

      <div className="glass-card rounded-xl p-5 mb-6">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">Permission matrix</div>
            <h2 className="text-lg font-semibold">Every system capability, every tier.</h2>
            <div className="text-xs text-slate-500 mt-1">Dashed cells inherit: Pro+ from Pro, Enterprise from Team. Setting one stores an override for that tier.</div>
          </div>
          <div className="flex items-center gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter capabilities…"
              className="bg-black/30 border border-white/10 rounded-md px-3 py-1.5 text-xs w-64 focus:outline-none focus:border-accent"
            />
            <label className="text-xs flex items-center gap-2 cursor-pointer text-slate-300">
              <input type="checkbox" checked={showOnlyOverridden} onChange={(e) => setShowOnlyOverridden(e.target.checked)} className="accent-violet-500" />
              Only show overrides
            </label>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/5" data-testid="capability-matrix">
          <div className="grid gap-3 px-4 py-3 bg-white/[0.03] border-b border-white/5 text-[10px] uppercase tracking-[0.16em] text-slate-400" style={matrixCols}>
            <div>Capability</div>
            {TIER_IDS.map((t) => (
              <div key={t} className="text-center">{tierName(t)}</div>
            ))}
          </div>

          {filteredCategories.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-slate-500">No capabilities match.</div>
          )}

          {filteredCategories.map(({ cat, items }) => (
            <div key={cat.key}>
              <div className="px-4 py-2.5 bg-black/30 border-b border-white/5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">{cat.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{cat.blurb}</div>
              </div>
              {items.map((cap: Capability) => {
                const overridden = !!capOverrides[cap.key]
                return (
                  <div key={cap.key} className={`grid gap-3 px-4 py-3 border-b border-white/5 items-center ${overridden ? 'bg-accent/[0.03]' : ''}`} style={matrixCols}>
                    <div>
                      <div className="text-sm text-slate-100 flex items-center gap-2">
                        {cap.label}
                        {cap.status && cap.status !== 'shipping' && (
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cap.status === 'beta' ? 'bg-indigo-500/15 text-indigo-300' : 'bg-amber-500/15 text-amber-300'}`}>
                            {cap.status === 'beta' ? 'Beta' : 'Soon'}
                          </span>
                        )}
                        {overridden && (<span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">overridden</span>)}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{cap.description}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5 font-mono">{cap.key}</div>
                    </div>
                    {TIER_IDS.map((tier) => (
                      <div key={tier} className="flex justify-center" data-testid={`cell-${cap.key}-${tier}`}>
                        <MatrixCell
                          value={tierDefault(cap, tier)}
                          inherited={inheritedFrom(cap, tier)}
                          disabled={writesBlocked}
                          onChange={(next) => patchCapability(cap.key, tier, next)}
                        />
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-xl p-5 text-xs text-slate-400 leading-relaxed">
        <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-2">How this connects to the rest of the system</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Price, tagline and listing edits PATCH <code className="text-slate-200">/admin/plans/:tier</code> (only the fields you changed). The brochure reads the merged result from <code className="text-slate-200">GET /public/plans</code> (edge-cached ~60s).</li>
          <li>Capability toggles PATCH <code className="text-slate-200">/admin/capabilities</code> and are read by the desktop app via <code className="text-slate-200">GET /account/capabilities</code> and by the brochure via <code className="text-slate-200">GET /public/capabilities</code>.</li>
          <li>Quantitative limits accept numbers or labels like <code className="text-slate-200">Unlimited</code>; the desktop app treats any non-number as "no limit".</li>
          <li><strong>Reset overrides</strong> clears every override (<code className="text-slate-200">DELETE /admin/plans</code> + <code className="text-slate-200">/admin/capabilities</code>). There is no per-tier clear on the server.</li>
        </ol>
      </div>
    </div>
  )
}
